// src/components/web-booking/CompanyConstraintsSection.js
// 「特殊日期 / 公休」區塊（Plan 094 Phase 2）— 嵌入 WebBookingSettingsPage。
//   固定公休（每週切換）+ 特殊日期 / 節日例外（CRUD）。
//   自我管理狀態 + 立即存檔（不依賴頁面的「儲存」按鈕）。
//   重用語音端的 ConstraintTable / ConstraintDeleteDialog（純呈現元件）。
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Stack, Snackbar, Alert, Switch, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import restaurantConstraintService from '../../services/restaurantConstraintService';
import ConstraintTable from '../voice-flow/ConstraintTable';
import ConstraintDeleteDialog from '../voice-flow/ConstraintDeleteDialog';
import WeeklyClosureToggle from './WeeklyClosureToggle';
import SpecialDateDialog from './SpecialDateDialog';

const BRAND_BLUE = '#145da0'; // 系統藍（新增特殊日期鈕 + 啟用開關統一用）

// 手機卡片用（桌機仍走 ConstraintTable，配色一致）
const TYPE_PILL = {
  blackout:      { label: '完全不接受', bg: '#fef0f0', bd: '#f5c2c7', color: '#c62828' },
  closed:        { label: '當日店休', bg: '#fff8e1', bd: '#ffe082', color: '#e65100' },
  limited_hours: { label: '部分時段不開放', bg: '#e3f2fd', bd: '#90caf9', color: '#1565c0' },
};
const RECURRING_LABELS = { yearly_by_date: '每年同日', yearly_by_lunar: '每年同農曆' };
const FALLBACK_LABELS = { human_callback: '回電真人', suggest_alt_date: '建議改日期' };
const fmtDateRange = (s, e) => (!s || !e ? '—' : (s === e ? s : `${s} ~ ${e}`));

// 訂單管理頁的單日下拉會建 name 形如 `closure-2026-08-18-all`（Plan 117）。
// 那個名字是給程式辨識來源用的，直接顯示給老闆看太機器 —— 這裡換成人看得懂的說法，
// 並標出它是從哪一頁設定的，讓「兩頁同步」這件事在畫面上是看得見的。
const DAY_CLOSURE_RE = /^closure-(\d{4})-(\d{2})-(\d{2})-(all|lunch|dinner)$/;
const DAY_MEAL_LABEL = { all: '全天', lunch: '午餐', dinner: '晚上' };
const displayName = (row) => {
  const m = DAY_CLOSURE_RE.exec(String(row.name || ''));
  if (!m) return row.name;
  return `${Number(m[2])}/${Number(m[3])} ${DAY_MEAL_LABEL[m[4]]}店休`;
};
const isFromReservationsPage = (row) => DAY_CLOSURE_RE.test(String(row.name || ''));
const fmtTimeRange = (allDay, s, e) => (allDay ? '整天' : (!s || !e ? '—' : `${String(s).slice(0, 5)} ~ ${String(e).slice(0, 5)}`));

const CompanyConstraintsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 手機改卡片列表（桌機維持表格）
  const [weekly, setWeekly] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [del, setDel] = useState({ open: false, row: null });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await restaurantConstraintService.list();
      setWeekly(body.weeklyClosedDays || []);
      setRows(body.specialDates || []);
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleWeekday = async (d) => {
    const next = weekly.includes(d) ? weekly.filter((x) => x !== d) : [...weekly, d];
    setWeekly(next); // 樂觀更新
    setBusy(true);
    try {
      const body = await restaurantConstraintService.setWeeklyClosures(next);
      setWeekly(body.weeklyClosedDays || []);
    } catch (e) {
      notify(e.message, 'error');
      load(); // 失敗回滾
    } finally {
      setBusy(false);
    }
  };

  const saveSpecial = async (payload) => {
    setBusy(true);
    try {
      if (dialog.initial) await restaurantConstraintService.update(dialog.initial.id, payload);
      else await restaurantConstraintService.create(payload);
      setDialog({ open: false, initial: null });
      notify('已儲存');
      await load();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const onToggleRow = async (row) => {
    setBusy(true);
    try { await restaurantConstraintService.toggle(row.id); await load(); }
    catch (e) { notify(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await restaurantConstraintService.remove(del.row.id);
      setDel({ open: false, row: null });
      notify('已刪除');
      await load();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2.5, borderRadius: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 0.5 }}>特殊日期 / 公休</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
        設定一次，<b>線上訂位與 AI 電話一致生效</b>。
      </Typography>

      {/* 固定公休（每週）*/}
      <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fcfdfe', mb: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>固定公休日（每週）</Typography>
        <WeeklyClosureToggle value={weekly} onToggle={toggleWeekday} busy={busy} />
      </Box>

      {/* 特殊日期 / 節日例外 */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>特殊日期 / 節日例外</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: '#0e4a80' }, textTransform: 'none' }}
          onClick={() => setDialog({ open: true, initial: null })}>
          新增特殊日期
        </Button>
      </Stack>

      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Typography sx={{ color: 'text.disabled', fontSize: 14, py: 2, textAlign: 'center' }}>載入中…</Typography>
          ) : !rows || rows.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, color: '#9aa7b6' }}>
              <CalendarMonthIcon sx={{ fontSize: 44, color: '#dbe2ea', display: 'block', mx: 'auto', mb: 1 }} />
              <Typography sx={{ fontSize: 14 }}>暫無特殊日期 — 點右上「新增特殊日期」</Typography>
            </Box>
          ) : rows.map((row) => {
            const t = TYPE_PILL[row.constraintType] || { label: row.constraintType, bg: '#f5f5f5', bd: '#e0e0e0', color: '#555' };
            const fb = FALLBACK_LABELS[row.fallbackAction];
            return (
              <Box key={row.id} onClick={() => setDialog({ open: true, initial: row })}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1.2, opacity: row.enabled ? 1 : 0.55, cursor: 'pointer' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName(row)}</Typography>
                  {isFromReservationsPage(row) && (
                    <Box component="span" sx={{ flex: '0 0 auto', fontSize: 11, fontWeight: 700, px: 0.9, py: '2px', borderRadius: '11px', border: '1px solid #cfd8dc', bgcolor: '#eceff1', color: '#546e7a', whiteSpace: 'nowrap' }}>訂單管理頁</Box>
                  )}
                  <Box component="span" sx={{ flex: '0 0 auto', fontSize: 11.5, fontWeight: 700, px: 1, py: '2px', borderRadius: '11px', border: '1px solid', bgcolor: t.bg, borderColor: t.bd, color: t.color, whiteSpace: 'nowrap' }}>{t.label}</Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px', mt: 1, fontSize: 12.5, color: '#475569' }}>
                  <span><Box component="span" sx={{ color: '#94a3b8' }}>期間 </Box>{fmtDateRange(row.startDate, row.endDate)}</span>
                  <span><Box component="span" sx={{ color: '#94a3b8' }}>時段 </Box>{fmtTimeRange(row.allDay, row.startTime, row.endTime)}</span>
                  <span><Box component="span" sx={{ color: '#94a3b8' }}>重複 </Box>{row.recurring ? (RECURRING_LABELS[row.recurring] || row.recurring) : '—'}</span>
                  {fb && <span><Box component="span" sx={{ color: '#94a3b8' }}>後續 </Box>{fb}</span>}
                </Box>
                {row.messageText && (
                  <Typography sx={{ mt: 0.75, fontSize: 12.5, color: '#8a97a6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.messageText}</Typography>
                )}
                <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.2, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Switch size="small" checked={!!row.enabled} onChange={() => onToggleRow(row)} disabled={busy}
                    sx={{ '& .Mui-checked': { color: BRAND_BLUE }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: BRAND_BLUE } }} />
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mr: 'auto' }}>{row.enabled ? '啟用' : '已停用'}</Typography>
                  <IconButton size="small" onClick={() => setDialog({ open: true, initial: row })} disabled={busy} sx={{ border: '1px solid', borderColor: 'divider' }}><EditIcon sx={{ fontSize: 18, color: '#03a9f4' }} /></IconButton>
                  <IconButton size="small" onClick={() => setDel({ open: true, row })} disabled={busy} sx={{ border: '1px solid', borderColor: 'divider' }}><DeleteIcon sx={{ fontSize: 18, color: '#e53935' }} /></IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <ConstraintTable
          rows={rows}
          renderName={(row) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <span>{displayName(row)}</span>
              {isFromReservationsPage(row) && (
                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 0.9, py: '2px', borderRadius: '11px', border: '1px solid #cfd8dc', bgcolor: '#eceff1', color: '#546e7a', whiteSpace: 'nowrap' }}>訂單管理頁</Box>
              )}
            </Box>
          )}
          loading={loading}
          busy={busy}
          onEdit={(row) => setDialog({ open: true, initial: row })}
          onDelete={(row) => setDel({ open: true, row })}
          onToggle={onToggleRow}
        />
      )}

      <SpecialDateDialog
        open={dialog.open}
        initial={dialog.initial}
        busy={busy}
        onClose={() => setDialog({ open: false, initial: null })}
        onSave={saveSpecial}
      />
      <ConstraintDeleteDialog
        open={del.open}
        constraint={del.row}
        loading={busy}
        onCancel={() => setDel({ open: false, row: null })}
        onConfirm={confirmDelete}
      />
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CompanyConstraintsSection;
